import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  verify: () => api.get('/auth/verify'),
};

export const moviesAPI = {
  getAll: (params = {}) => api.get('/movies', { params }),
  getWithShowtimes: () => api.get('/movies/with-showtimes'),
  getById: (id) => api.get(`/movies/${id}`),
  create: (data) => api.post('/movies', data),
  update: (id, data) => api.put(`/movies/${id}`, data),
  delete: (id) => api.delete(`/movies/${id}`),
};

export const branchesAPI = {
  getAll: () => api.get('/branches'),
  getById: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
};

export const showtimesAPI = {
  getAll: (params) => api.get('/showtimes', { params }),
  getById: (id) => api.get(`/showtimes/${id}`),
  create: (data) => api.post('/showtimes', data),
  createBulk: (data) => api.post('/showtimes/bulk', data),
  update: (id, data) => api.put(`/showtimes/${id}`, data),
  delete: (id) => api.delete(`/showtimes/${id}`),
  bulkDelete: (ids) => api.post('/showtimes/bulk-delete', ids),
  bulkUpdate: (data) => api.post('/showtimes/bulk-update', data),
};

export const campaignsAPI = {
  getAll: () => api.get('/campaigns'),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

export const eventsAPI = {
  getAll: () => api.get('/events'),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

export const galleryAPI = {
  getAll: (category) => api.get('/gallery', { params: { category } }),
  create: (data) => api.post('/gallery', data),
  delete: (id) => api.delete(`/gallery/${id}`),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

export const pagesAPI = {
  getAll: (params) => api.get('/pages', { params }),
  getBySlug: (slug) => api.get(`/pages/${slug}`),
  create: (data) => api.post('/pages', data),
  update: (id, data) => api.put(`/pages/${id}`, data),
  delete: (id) => api.delete(`/pages/${id}`),
};

export const adminAPI = {
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const smartRatingsAPI = {
  getAll: () => api.get('/smart-ratings'),
  create: (data) => api.post('/smart-ratings', data),
  update: (id, data) => api.put(`/smart-ratings/${id}`, data),
  delete: (id) => api.delete(`/smart-ratings/${id}`),
};

export const contactMessagesAPI = {
  create: (data) => axios.post(`${API}/contact-messages`, data), // Public endpoint - no auth
  getAll: () => api.get('/contact-messages'),
  markAsRead: (id) => api.patch(`/contact-messages/${id}/read`),
  delete: (id) => api.delete(`/contact-messages/${id}`),
};


export const contactSubjectsAPI = {
  getAll: (params) => axios.get(`${API}/contact-subjects`, { params }), // Public endpoint
  create: (data) => api.post('/contact-subjects', data),
  update: (id, data) => api.put(`/contact-subjects/${id}`, data),
  delete: (id) => api.delete(`/contact-subjects/${id}`),
};

export const showtimesImportAPI = {
  validateImport: (fileBase64) => api.post('/showtimes/validate-import', { file: fileBase64 }),
  importFromFile: (fileBase64, movieMappings = {}, branchMappings = {}) => 
    api.post('/showtimes/import', { 
      file: fileBase64, 
      movie_mappings: movieMappings,
      branch_mappings: branchMappings 
    }),
};

export const databaseExportAPI = {
  getStats: () => api.get('/admin/export-database-stats'),
  exportDatabase: () => api.get('/admin/export-database'),
};

export const analyticsAPI = {
  trackPageview: (data) => api.post('/analytics/pageview', data),
  trackEvent: (data) => api.post('/analytics/event', data),
  trackAdminAction: (data) => api.post('/analytics/admin-action', data),
  getDashboard: (params = {}) => api.get('/analytics/dashboard', { params }),
  getRealtime: () => api.get('/analytics/realtime'),
};

export default api;
